import { PageHeader } from "@layouts/PageHeader";
import { Header } from "./Header";
import { UICardWrap } from "./UICardWrap";
import { KdAlert } from "@kalodata/kds-design";
import { IconSolidHorn } from "@kalodata/kds-design-icons";
import { Trans } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { useSetAtom } from "jotai";
import { notificationModalAtom } from "@src/jotai/notification";
import { getNotificationPermission } from "@utils/notification";
import { useAppSelector } from "@hooks/redux";
import { getViewedCards } from "./librarySlice";
import type { UserAssetV2Record } from "@apis/http/api";
import GlobalVideoDetailModal from "@layouts/GlobalVideoDetailModal";
import { getNewGeneratedCardsCount, isProcessingAsset } from "./hooks/useGeneratedVideoAssets";
import { useLibraryFilters } from "./hooks/useLibraryFilters";
import { useImageBatchSelection } from "./hooks/useImageBatchSelection";
import { useVideoBatchSelection } from "./hooks/useVideoBatchSelection";

const noopRefresh = () => {};

export const Library = () => {
  const setNotificationModal = useSetAtom(notificationModalAtom);
  const [notificationGranted, setNotificationGranted] = useState(() => getNotificationPermission() === "granted");
  const { lastViewedTimestamp } = useAppSelector((state) => state.libraryReducer);
  const [generatedItems, setGeneratedItems] = useState<UserAssetV2Record[]>([]);
  const [refreshGeneratedItems, setRefreshGeneratedItems] = useState<() => void>(() => noopRefresh);
  const [imageItems, setImageItems] = useState<UserAssetV2Record[]>([]);
  const [refreshImageItems, setRefreshImageItems] = useState<() => void>(() => noopRefresh);
  const { focusType, videoType } = useLibraryFilters();
  const imageSelection = useImageBatchSelection(imageItems, focusType);
  const [uploadedVideoItems, setUploadedVideoItems] = useState<UserAssetV2Record[]>([]);
  const [refreshUploadedVideos, setRefreshUploadedVideos] = useState<() => void>(() => noopRefresh);
  const activeVideoItems = useMemo(() => {
    if (videoType === "generated") return generatedItems;
    if (videoType === "uploaded") return uploadedVideoItems;
    return [];
  }, [videoType, generatedItems, uploadedVideoItems]);
  const videoSelection = useVideoBatchSelection(activeVideoItems, focusType, videoType);
  const processingTasks = useMemo(() => generatedItems.filter(isProcessingAsset), [generatedItems]);
  const viewedIds = useMemo(() => getViewedCards(), [lastViewedTimestamp]);
  const newCardsCount = useMemo(() => {
    return getNewGeneratedCardsCount(generatedItems, viewedIds);
  }, [viewedIds, generatedItems]);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalIndex, setDetailModalIndex] = useState(0);

  const handleVideoDetailClick = useCallback((_record: UserAssetV2Record, index: number) => {
    setDetailModalIndex(index);
    setDetailModalOpen(true);
  }, []);
  const handleGeneratedRefreshChange = useCallback((refresh: () => void) => {
    setRefreshGeneratedItems(() => refresh);
  }, []);
  const handleImageRefreshChange = useCallback((refresh: () => void) => {
    setRefreshImageItems(() => refresh);
  }, []);
  const handleImageBatchDeleted = useCallback(() => {
    refreshImageItems();
  }, [refreshImageItems]);

  const handleUploadedVideoItemsChange = useCallback((items: UserAssetV2Record[]) => {
    setUploadedVideoItems(items);
  }, []);

  const handleUploadedVideoRefreshChange = useCallback((refresh: () => void) => {
    setRefreshUploadedVideos(() => refresh);
  }, []);

  const handleVideoBatchDeleted = useCallback(() => {
    if (videoType === "generated") refreshGeneratedItems();
    else if (videoType === "uploaded") refreshUploadedVideos();
  }, [videoType, refreshGeneratedItems, refreshUploadedVideos]);

  return (
    <div className="flex flex-col h-screen">
      <PageHeader title="Library" showBack={false}></PageHeader>
      {!notificationGranted && processingTasks.length > 0 && (
        <KdAlert
          type="info"
          closable
          className="rounded-none border-none px-[16px] py-[14px] gap-[8px]"
          icon={<IconSolidHorn size={20} className="text-kds-accent-fg" />}
          content={
            <div className="py-[4px] text-[14px] leading-[1.4] text-kds-fg">
              <Trans
                components={{
                  span: (
                    <span
                      className="font-bold cursor-pointer hover:underline"
                      onClick={() => {
                        setNotificationModal({
                          open: true,
                          onComplete: () => {
                            if (getNotificationPermission() === "granted") {
                              setNotificationGranted(true);
                            }
                          },
                        });
                      }}
                    />
                  ),
                }}
                defaults="Don't wait! Start a new task. <span>Enable notifications</span> to know when it's done."
              />
            </div>
          }
        />
      )}
      <Header
        className="py-[16px] px-[24px]"
        processingCount={processingTasks.length}
        newCardsCount={newCardsCount}
        imageSelection={imageSelection}
        onImageBatchDeleted={handleImageBatchDeleted}
        videoSelection={videoSelection}
        onVideoBatchDeleted={handleVideoBatchDeleted}
      />
      <div className="flex-1 p-[24px] pt-0 overflow-y-auto scrollbar">
        <UICardWrap
          onVideoDetailClick={handleVideoDetailClick}
          onGeneratedItemsChange={setGeneratedItems}
          onGeneratedRefreshChange={handleGeneratedRefreshChange}
          imageSelection={imageSelection}
          onImageItemsChange={setImageItems}
          onImageRefreshChange={handleImageRefreshChange}
          videoSelection={videoSelection}
          onUploadedVideoItemsChange={handleUploadedVideoItemsChange}
          onUploadedVideoRefreshChange={handleUploadedVideoRefreshChange}
        ></UICardWrap>
      </div>
      <GlobalVideoDetailModal
        open={detailModalOpen}
        records={generatedItems}
        initialIndex={detailModalIndex}
        onRefresh={refreshGeneratedItems}
        onClose={() => {
          setDetailModalOpen(false);
          refreshGeneratedItems();
        }}
      />
    </div>
  );
};
